import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  createAccount,
  authStatusSelector,
  authErrorSelector,
  clearError,
  currentUserSelector,
} from '../redux/slices/userSlice';

/**
 * Custom hook to handle create account input and submission.
 *
 * @returns username - The current value of the username input.
 * @returns password - The current value of the password input.
 * @returns profile - The current value of the profile input.
 * @returns handleInputChange - Function to handle changes in the input field.
 * @returns handleSubmit - Function to handle create an account submission.
 * @returns handleLoginNav - Function to navigate to login page.
 * @returns handleProfileSelect - Function to handle profile picture selection.
 * @returns error - The error message from the signup attempt, if any.
 * @returns status - The current status of the signup attempt.
 */
const useCreateAccount = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [profile, setProfile] = useState<string>('profile1.png');
  const [success, setSuccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Access authentication status and error from Redux
  const status = useAppSelector(authStatusSelector);
  const error = useAppSelector(authErrorSelector);
  const currentUser = useAppSelector(currentUserSelector);

  useEffect(() => {
    if (status === 'idle' && currentUser) {
      // Only navigate if authentication is confirmed (authStatus is not 'loading')
      navigate('/home', { replace: true });
    }
  }, [status, currentUser, navigate]);

  /**
   * Function to handle the input change event.
   *
   * @param e - the event object.
   * @param field - the field to update.
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, field: 'username' | 'password') => {
    const { value } = e.target;
    if (field === 'username') setUsername(value);
    else if (field === 'password') setPassword(value);
  };

  /**
   * Function to handle the form submission event.
   *
   * @param event - the form event object.
   */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setSuccess(false); // Reset success state
    await dispatch(clearError()); // Clear any existing error

    // Dispatch the signup action with username, password, and profile picture
    const action = await dispatch(createAccount({ username, password, profilePicture: profile }));

    // If the signup was successful, navigate to the home page
    if (createAccount.fulfilled.match(action)) {
      setSuccess(true);
    }
  };

  /**
   * Function to handle navigation to the login page.
   */
  const handleLoginNav = () => {
    dispatch(clearError()); // Clear any existing error
    navigate('/');
  };

  /**
   * Function to handle the profile selection event.
   * @param picture - the selected profile picture URL.
   */
  const handleProfileSelect = (pictureUrl: string) => {
    setProfile(pictureUrl);
  };

  return {
    username,
    password,
    profile,
    handleInputChange,
    handleSubmit,
    handleLoginNav,
    handleProfileSelect,
    status,
    error,
    success,
  };
};

export default useCreateAccount;
